/**
 * The assistant conversation, as the support panel's "Ask Artemis" tab shows it.
 *
 * It reads as the signed-in user and changes nothing (see
 * docs/architecture/user-scoped-assistant.md). A message starts a turn on the
 * server; AssistantProvider polls it and holds it while the tab is closed.
 * "Talk to a person" hands the whole chat to support as a new ticket.
 */
import AddCommentIcon from "@mui/icons-material/AddComment";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { FormEvent, useEffect, useRef, useState } from "react";
import {
	type AssistantConversation,
	type AssistantMessage,
	createAssistantConversation,
	escalateAssistantConversation,
	sendAssistantMessage,
} from "../../api/assistant/assistant";
import { AssistantMessageRole } from "../../api/generated/django-enums";
import { deriveTitle, describeReportContext } from "../support/support-report-context";
import { useAssistant } from "./assistant-context";
import { AssistantMarkdown } from "./assistant-markdown";
import { outlineCurrentPage } from "./assistant-page-outline";

/** What the most-read routes are called on screen; any other shows its path segment. */
const READ_LABELS: Record<string, string> = {
	assignment: "roles",
	personassignment2: "candidates",
	"todo-items": "tasks",
};

/** "/api/v2/assignment/?status__in=1" reads as "roles". */
const readLabel = (path: string): string => {
	const resource = path.replace(/^\/api\/v2\//, "").split(/[/?]/)[0];
	return READ_LABELS[resource] ?? resource;
};

const MessageEntry = ({ message }: { message: AssistantMessage }) =>
	message.role === AssistantMessageRole.USER ? (
		<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
			<Paper
				elevation={0}
				sx={{
					px: 1.5,
					py: 1,
					maxWidth: "88%",
					bgcolor: "primary.main",
					color: "primary.contrastText",
					borderRadius: 2,
					whiteSpace: "pre-wrap",
				}}
				data-component="assistant-user-message"
			>
				<Typography variant="body2">{message.text}</Typography>
			</Paper>
		</Box>
	) : (
		<Stack spacing={1} data-component="assistant-reply">
			{message.reads.map((path, index) => (
				<Tooltip key={`${message.id}-${index}`} title={path}>
					<Chip
						size="small"
						variant="outlined"
						icon={<SearchIcon />}
						label={`Read ${readLabel(path)}`}
						sx={{ alignSelf: "flex-start" }}
						data-component="assistant-read-chip"
					/>
				</Tooltip>
			))}
			{message.text && (
				<Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 2 }}>
					<AssistantMarkdown content={message.text} />
				</Paper>
			)}
		</Stack>
	);

/** The turn ended without a reply after the user's last message. */
const turnFailed = (conversation: AssistantConversation): boolean => {
	const last = conversation.messages.at(-1);
	return (
		!conversation.running &&
		!conversation.awaiting_view &&
		last !== undefined &&
		last.role === AssistantMessageRole.USER
	);
};

/** The user's first question, which titles the ticket the chat becomes. */
const firstQuestion = (conversation: AssistantConversation): string | undefined =>
	conversation.messages.find((message) => message.role === AssistantMessageRole.USER)
		?.text;

export const AssistantChat = ({
	onEscalated,
}: {
	/** The chat is a support ticket now: open it. */
	onEscalated: (ticketId: number) => void;
}) => {
	const { conversation, setConversation, error, setError } = useAssistant();
	const [draft, setDraft] = useState("");
	const [sending, setSending] = useState(false);
	const bottomRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ block: "end" });
	}, [conversation?.messages.length, conversation?.running]);

	const send = async (event: FormEvent) => {
		event.preventDefault();
		const text = draft.trim();
		if (!text) {
			return;
		}
		setSending(true);
		setError(null);
		try {
			const target = conversation ?? (await createAssistantConversation());
			setConversation(
				await sendAssistantMessage(
					target.id,
					text,
					`${window.location.pathname}${window.location.search}`,
					outlineCurrentPage(target.outline_max_chars)
				)
			);
			setDraft("");
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setSending(false);
		}
	};

	const busy =
		sending ||
		Boolean(conversation?.running) ||
		Boolean(conversation?.awaiting_view);
	const question = conversation ? firstQuestion(conversation) : undefined;

	const talkToPerson = async () => {
		if (!conversation || !question) return;
		setSending(true);
		setError(null);
		try {
			const ticketId = await escalateAssistantConversation(
				conversation.id,
				deriveTitle(question),
				describeReportContext()
			);
			// Handed over: the next question starts a new chat.
			setConversation(null);
			onEscalated(ticketId);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setSending(false);
		}
	};

	return (
		<Box
			sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
			data-component="assistant-chat"
		>
			<Stack spacing={1.5} sx={{ p: 2, flex: 1, overflowY: "auto" }}>
				{conversation === null || conversation.messages.length === 0 ? (
					<Typography
						variant="body2"
						color="text.secondary"
						data-component="assistant-empty"
					>
						Ask about your roles, your candidates, your tasks or how Artemis
						works.
					</Typography>
				) : (
					conversation.messages.map((message) => (
						<MessageEntry key={message.id} message={message} />
					))
				)}
				{(conversation?.running || conversation?.awaiting_view) && (
					<Stack
						direction="row"
						spacing={1}
						alignItems="center"
						data-component="assistant-thinking"
					>
						<CircularProgress size={14} />
						<Typography variant="caption" color="text.secondary">
							{conversation.awaiting_view
								? "Looking at your screen…"
								: "Reading…"}
						</Typography>
					</Stack>
				)}
				{conversation !== null && turnFailed(conversation) && (
					<Alert severity="warning" data-component="assistant-turn-failed">
						The assistant could not answer. Send your question again, or
						talk to a person.
					</Alert>
				)}
				{error && (
					<Alert severity="error" data-component="assistant-error">
						{error}
					</Alert>
				)}
				<div ref={bottomRef} />
			</Stack>
			{question && (
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					sx={{ px: 1.5, py: 0.5, borderTop: 1, borderColor: "divider" }}
				>
					<Tooltip title="Send this whole conversation to Artemis support and continue there">
						<span>
							<Button
								size="small"
								startIcon={<SupportAgentIcon />}
								disabled={busy}
								onClick={() => void talkToPerson()}
								data-component="assistant-talk-to-person"
							>
								Talk to a person
							</Button>
						</span>
					</Tooltip>
					<Tooltip title="New conversation">
						<span>
							<IconButton
								size="small"
								aria-label="New conversation"
								disabled={busy}
								onClick={() => {
									setConversation(null);
									setError(null);
								}}
								data-component="assistant-new-conversation"
							>
								<AddCommentIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
			)}
			<Stack
				component="form"
				direction="row"
				spacing={1}
				onSubmit={send}
				sx={{ px: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}
				data-component="assistant-composer"
			>
				<TextField
					size="small"
					fullWidth
					multiline
					maxRows={6}
					placeholder="Ask about your roles and candidates…"
					value={draft}
					onChange={(event) => setDraft(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === "Enter" && !event.shiftKey) {
							void send(event);
						}
					}}
					disabled={busy}
					data-component="assistant-input"
				/>
				<IconButton
					type="submit"
					color="primary"
					aria-label="Send"
					disabled={busy || !draft.trim()}
					data-component="assistant-send"
				>
					<SendIcon />
				</IconButton>
			</Stack>
			<Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
				Sees only what you can see. Cannot change anything.
			</Typography>
		</Box>
	);
};
